import React, { useState, useEffect, useRef, useContext, useMemo, useCallback } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";
import { head, isNil } from "lodash";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import CircularProgress from "@material-ui/core/CircularProgress";
import AttachFileIcon from "@material-ui/icons/AttachFile";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import Chip from '@material-ui/core/Chip';
import RepeatIcon from '@material-ui/icons/Repeat';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { i18n } from "../../translate/i18n";
import moment from "moment-timezone";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Tabs,
  FormControlLabel,
  Switch,
  Typography,
  Collapse,
  List,
  ListItem,
  ListItemText,
  FormHelperText,
  Card,
  CardContent,
  Checkbox,
  FormGroup,
} from "@material-ui/core";
import { AuthContext } from "../../context/Auth/AuthContext";
import ConfirmationModal from "../ConfirmationModal";
import UserStatusIcon from "../UserModal/statusIcon";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import useQueues from "../../hooks/useQueues";
import ContactGroupModal from "../ContactGroupModal";
import TemplatePreview from "../TemplatePreview";
import MessagePreview from "../MessagePreview";
import CampaignContactSelector from "../CampaignContactSelector";


const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  textField: {
    marginRight: theme.spacing(1),
    flex: 1,
  },
  extraAttr: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  recurrenceCard: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[50],
  },
  previewCard: {
    marginTop: theme.spacing(1),
    maxHeight: 200,
    overflow: 'auto',
  },
  recurrenceIcon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main,
  },
}));


// Componente isolado para o slider de jitter — evita crash por Fragment dentro de IIFE
const JitterSlider = ({ campaignSettings, setCampaignSettings }) => {
  const jitter = typeof campaignSettings.jitterPercent === 'number' ? campaignSettings.jitterPercent : 40;
  const base = typeof campaignSettings.messageInterval === 'number' ? campaignSettings.messageInterval : 0;
  const min = base > 0 ? Math.round(base * (1 - jitter / 100)) : 0;
  const max = base > 0 ? Math.round(base * (1 + jitter / 100)) : 0;

  return (
    <div style={{ padding: "0 4px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 12, color: "#555" }}>{i18n.t("campaignModal.jitterLabel")}</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#1976d2" }}>±{jitter}%</span>
      </div>
      <input
        type="range"
        min={0}
        max={80}
        step={5}
        value={jitter}
        onChange={(e) => {
          const val = Number(e.target.value);
          setCampaignSettings((p) => ({ ...p, jitterPercent: val }));
        }}
        style={{ width: "100%", accentColor: "#1976d2" }}
      />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: 10, color: "#aaa" }}>0% {i18n.t("campaignModal.jitterFixed")}</span>
        <span style={{ fontSize: 10, color: "#aaa" }}>80% {i18n.t("campaignModal.jitterMax")}</span>
      </div>
      {base > 0 ? (
        <p style={{ fontSize: 11, color: "#888", margin: "4px 0 0" }}>
          {i18n.t("campaignModal.jitterBase", { base, min, max })}
        </p>
      ) : (
        <p style={{ fontSize: 11, color: "#e65100", margin: "4px 0 0" }}>
          {i18n.t("campaignModal.jitterSelectBase")}
        </p>
      )}
    </div>
  );
};

// No CampaignModal.js - Atualizar o schema de validação

const CampaignSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, i18n.t("campaignModal.validation.tooShort"))
    .max(50, i18n.t("campaignModal.validation.tooLong"))
    .required(i18n.t("campaignModal.validation.required")),
  isRecurring: Yup.boolean().default(false),
  recurrenceType: Yup.string().when('isRecurring', {
    is: true,
    then: Yup.string().oneOf(['minutely', 'hourly', 'daily', 'weekly', 'biweekly', 'monthly', 'yearly']).required(i18n.t("campaignModal.validation.recurrenceTypeRequired")),
    otherwise: Yup.string().nullable()
  }),
  recurrenceInterval: Yup.number().when('isRecurring', {
    is: true,
    then: Yup.number().min(1, i18n.t("campaignModal.validation.intervalMin")).required(i18n.t("campaignModal.validation.intervalRequired")),
    otherwise: Yup.number().nullable()
  }),
  recurrenceDaysOfWeek: Yup.array().when(['isRecurring', 'recurrenceType'], {
    is: (isRecurring, recurrenceType) => isRecurring && recurrenceType === 'weekly',
    then: Yup.array().min(1, i18n.t("campaignModal.validation.selectDay")).required(),
    otherwise: Yup.array().nullable()
  }),
  recurrenceDayOfMonth: Yup.number().when(['isRecurring', 'recurrenceType'], {
    is: (isRecurring, recurrenceType) => isRecurring && recurrenceType === 'monthly',
    then: Yup.number().min(1, i18n.t("campaignModal.validation.dayRange")).max(31, i18n.t("campaignModal.validation.dayRange")).required(i18n.t("campaignModal.validation.dayRequired")),
    otherwise: Yup.number().nullable()
  }),
  recurrenceEndDate: Yup.date().when('isRecurring', {
    is: true,
    then: Yup.date().min(new Date(), i18n.t("campaignModal.validation.endDateFuture")).nullable(),
    otherwise: Yup.date().nullable()
  }),
  maxExecutions: Yup.number().when('isRecurring', {
    is: true,
    then: Yup.number().min(1, i18n.t("campaignModal.validation.maxExecMin")).nullable(),
    otherwise: Yup.number().nullable()
  })
});

const CampaignModal = ({
  open,
  onClose,
  campaignId,
  initialValues,
  onSave,
  resetPagination,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.mode === "dark" || theme.palette?.type === "dark";
  const isMounted = useRef(true);
  const { user, socket } = useContext(AuthContext);
  const { companyId } = user;
  const userTimezone = user?.timezone || moment.tz.guess() || "America/Sao_Paulo";

  const initialState = {
    name: "",
    message1: "",
    message2: "",
    message3: "",
    message4: "",
    message5: "",
    confirmationMessage1: "",
    confirmationMessage2: "",
    confirmationMessage3: "",
    confirmationMessage4: "",
    confirmationMessage5: "",
    status: "INATIVA",
    confirmation: false,
    scheduledAt: "",
    contactListId: "",
    tagListId: "none",
    companyId,
    statusTicket: "closed",
    openTicket: "disabled",
    // Novos campos de recorrência
    isRecurring: false,
    recurrenceType: "",
    recurrenceInterval: 1,
    recurrenceDaysOfWeek: [],
    recurrenceDayOfMonth: 1,
    recurrenceEndDate: "",
    maxExecutions: null,
    sendFollowUpMessage: false,
    followUpDelay: 3,
    messageDelays: { message1: 3, message2: 3, message3: 3, message4: 3, message5: 3 },
  };

  const [campaign, setCampaign] = useState(initialState);
  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsapps, setSelectedWhatsapps] = useState([]);
 const [whatsappId, setWhatsappId] = useState(null);
  const [contactLists, setContactLists] = useState([]);
  const [tagLists, setTagLists] = useState([]);
  const [messageTab, setMessageTab] = useState(0);
  const [attachment, setAttachment] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [campaignEditable, setCampaignEditable] = useState(true);
  const [previewExecutions, setPreviewExecutions] = useState([]);
  const [showPreview, setShowPreview] = useState(false);
  const [messageMedias, setMessageMedias] = useState({}); // { message1: File, message2: File, ... }
  const [savedMessageMedias, setSavedMessageMedias] = useState({}); // { message1: { path, name }, ... }
  // Templates aprovados vinculados a slots de follow-up (API Oficial). Quando definido,
  // o slot envia o template em vez de texto livre. Formato: { message2: { templateId, name } }
  const [messageTemplates, setMessageTemplates] = useState({});
  const attachmentFile = useRef(null);
  const messageMediaRefs = useRef({});

  const [options, setOptions] = useState([]);
  const [queues, setQueues] = useState([]);
  const [allQueues, setAllQueues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState(null);
  const { findAll: findAllQueues } = useQueues();

  // Configurações de campanha (antes em CampaignsConfig)
  const initialCampaignSettings = {
    messageInterval: 20,
    longerIntervalAfter: 20,
    greaterInterval: 60,
    jitterPercent: 40,
    longPauseEvery: 50,
    longPauseDuration: 30,
  };
  const [campaignSettings, setCampaignSettings] = useState(initialCampaignSettings);
  const [savingSettings, setSavingSettings] = useState(false);

  // API Oficial - Templates
  const [isOficialSelected, setIsOficialSelected] = useState(false);
  const [oficialTemplates, setOficialTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [contactGroupModalOpen, setContactGroupModalOpen] = useState(false);
  // Persistência por template: ao trocar de template e voltar, os valores
  // já preenchidos não são perdidos enquanto o modal estiver aberto.
  const [templateVarValuesByTemplate, setTemplateVarValuesByTemplate] = useState({});
  const templateVarValues = useMemo(
    () => (selectedTemplateId ? (templateVarValuesByTemplate[selectedTemplateId] || {}) : {}),
    [templateVarValuesByTemplate, selectedTemplateId]
  );
  const setTemplateVarValues = useCallback((updater) => {
    setTemplateVarValuesByTemplate(prev => {
      if (!selectedTemplateId) return prev;
      const current = prev[selectedTemplateId] || {};
      const next = typeof updater === "function" ? updater(current) : updater;
      return { ...prev, [selectedTemplateId]: next };
    });
  }, [selectedTemplateId]);
  const [individualContacts, setIndividualContacts] = useState([]);

  // Opções para dias da semana
  const daysOfWeekOptions = [
    { value: 0, label: i18n.t("campaignModal.daysOfWeekOptions.sunday") },
    { value: 1, label: i18n.t("campaignModal.daysOfWeekOptions.monday") },
    { value: 2, label: i18n.t("campaignModal.daysOfWeekOptions.tuesday") },
    { value: 3, label: i18n.t("campaignModal.daysOfWeekOptions.wednesday") },
    { value: 4, label: i18n.t("campaignModal.daysOfWeekOptions.thursday") },
    { value: 5, label: i18n.t("campaignModal.daysOfWeekOptions.friday") },
    { value: 6, label: i18n.t("campaignModal.daysOfWeekOptions.saturday") },
  ];

  // Função para preview das execuções
  const handlePreviewRecurrence = async (values) => {
    if (!values.isRecurring || !values.recurrenceType || !values.scheduledAt) {
      setPreviewExecutions([]);
      return;
    }

    try {
      const params = {
        recurrenceType: values.recurrenceType,
        recurrenceInterval: values.recurrenceInterval,
        recurrenceDaysOfWeek: JSON.stringify(values.recurrenceDaysOfWeek),
        recurrenceDayOfMonth: values.recurrenceDayOfMonth,
      };

      if (campaignId) {
        const { data } = await api.get(`/campaigns/${campaignId}/recurrence-preview`, { params });
        setPreviewExecutions(data.executions);
      } else {
        // Para campanhas novas, calcular localmente ou usar endpoint genérico
        const mockExecutions = calculateMockExecutions(values);
        setPreviewExecutions(mockExecutions);
      }
    } catch (err) {
      console.error('Erro ao buscar preview:', err);
    }
  };

  // Função auxiliar para calcular execuções mock
  const calculateMockExecutions = (values) => {
    const executions = [];
    let currentDate = moment(values.scheduledAt);
    
    for (let i = 0; i < 5; i++) {
      executions.push(currentDate.format('DD/MM/YYYY HH:mm'));
      
      switch (values.recurrenceType) {
        case 'minutely':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'minutes');
          break;
        case 'hourly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'hours');
          break;
        case 'daily':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'days');
          break;
        case 'weekly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'weeks');
          break;
        case 'biweekly':
          currentDate = currentDate.clone().add(values.recurrenceInterval * 2, 'weeks');
          break;
        case 'monthly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'months');
          if (values.recurrenceDayOfMonth) {
            currentDate = currentDate.date(values.recurrenceDayOfMonth);
          }
          break;
        case 'yearly':
          currentDate = currentDate.clone().add(values.recurrenceInterval, 'years');
          break;
      }
    }
    
    return executions;
  };

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current) {
      const loadQueues = async () => {
        const list = await findAllQueues();
        setAllQueues(list);
        setQueues(list);
      };
      loadQueues();
    }
  }, []);

  useEffect(() => {
    if (searchParam.length < 3) {
      setLoading(false);
      setSelectedQueue("");
      return;
    }
    const delayDebounceFn = setTimeout(() => {
      setLoading(true);
      const fetchUsers = async () => {
        try {
          const { data } = await api.get("/users/");
          setOptions(data.users);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchUsers();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam]);

  useEffect(() => {
    if (isMounted.current) {
      if (initialValues) {
        setCampaign((prevState) => {
          return { ...prevState, ...initialValues };
        });
      }

      api
        .get(`/contact-lists/list`, { params: { companyId } })
        .then(({ data }) => setContactLists(data));

      api
        .get(`/whatsapp`, { params: { companyId, session: 0 } })
        .then(({ data }) => {
          const mappedWhatsapps = data.map((whatsapp) => ({
            ...whatsapp,
            selected: false,
          }));
          setWhatsapps(mappedWhatsapps);
        });

      api.get(`/tags/list`, { params: { companyId } })
        .then(({ data }) => {
          const fetchedTags = Array.isArray(data) ? data : [];
          const formattedTagLists = fetchedTags
            .map((tag) => {
              const count = Array.isArray(tag.contacts) ? tag.contacts.length : 0;
              return {
                id: tag.id,
                name: count > 0 ? `${tag.name} (${count})` : tag.name,
                contactsCount: count,
              };
            })
            // Mostra todas as tags da empresa (kanban e não-kanban) para que apareçam
            // sempre que estiverem cadastradas, mesmo se a contagem vier como 0.
            .sort((a, b) => (b.contactsCount || 0) - (a.contactsCount || 0));
          setTagLists(formattedTagLists);
        })
        .catch((error) => {
          console.error("Error retrieving tags:", error);
        });

      // Carregar configurações de campanha
      const numericKeys = ["messageInterval", "longerIntervalAfter", "greaterInterval", "jitterPercent", "longPauseEvery", "longPauseDuration"];
      api.get("/campaign-settings").then(({ data }) => {
        if (Array.isArray(data) && data.length > 0) {
          const settingsList = [];
          data.forEach((item) => {
            const val = numericKeys.includes(item.key) ? Number(item.value) : item.value;
            settingsList.push([item.key, val]);
          });
          setCampaignSettings((prev) => ({ ...prev, ...Object.fromEntries(settingsList) }));
        }
      });

      if (!campaignId) return;

      api.get(`/campaigns/${campaignId}`).then(({ data }) => {
        if (data?.user) setSelectedUser(data.user);
        if (data?.queue) setSelectedQueue(data.queue.id);
        if (data?.whatsappId) {
          setWhatsappId(parseInt(data.whatsappId));
        } else {
          setWhatsappId(null);
        }
        if (data?.templateId) {
          setSelectedTemplateId(data.templateId);
          if (data.templateVariables) {
            try {
              const parsed = JSON.parse(data.templateVariables);
              if (parsed && typeof parsed === "object") {
                setTemplateVarValuesByTemplate(prev => ({
                  ...prev,
                  [data.templateId]: parsed,
                }));
              }
            } catch (_) { /* ignore */ }
          }
        }
        
        setCampaign((prev) => {
          let prevCampaignData = Object.assign({}, prev);

          Object.entries(data).forEach(([key, value]) => {
            if (key === "scheduledAt" && value !== "" && value !== null) {
              // Backend devolve UTC (ISO). Converte para o fuso do usuário antes de exibir no input.
              prevCampaignData[key] = moment.utc(value).tz(userTimezone).format("YYYY-MM-DDTHH:mm");
            } else if (key === "recurrenceEndDate" && value !== "" && value !== null) {
              prevCampaignData[key] = moment.utc(value).tz(userTimezone).format("YYYY-MM-DD");
            } else if (key === "recurrenceDaysOfWeek" && value) {
              prevCampaignData[key] = JSON.parse(value);
            } else if (key === "messageDelays") {
              try {
                prevCampaignData[key] = value ? JSON.parse(value) : { message1: 3, message2: 3, message3: 3, message4: 3, message5: 3 };
              } catch (e) {
                prevCampaignData[key] = { message1: 3, message2: 3, message3: 3, message4: 3, message5: 3 };
              }
            } else if (key === "messageMedias") {
              try {
                setSavedMessageMedias(value ? JSON.parse(value) : {});
              } catch (e) {
                setSavedMessageMedias({});
              }
            } else if (key === "messageTemplates") {
              try {
                setMessageTemplates(value ? JSON.parse(value) : {});
              } catch (e) {
                setMessageTemplates({});
              }
            } else {
              prevCampaignData[key] = value === null ? "" : value;
            }
          });

          // Garantir que messageDelays nunca seja null/undefined
          if (!prevCampaignData.messageDelays || typeof prevCampaignData.messageDelays !== "object") {
            prevCampaignData.messageDelays = { message1: 3, message2: 3, message3: 3, message4: 3, message5: 3 };
          }

          return prevCampaignData;
        });
      });
    }
  }, [campaignId, open, initialValues, companyId]);

  // Detectar se o WhatsApp selecionado é API Oficial e buscar templates
  useEffect(() => {
    if (!whatsappId || whatsapps.length === 0) {
      setIsOficialSelected(false);
      setOficialTemplates([]);
      return;
    }
    const selectedWpp = whatsapps.find(w => w.id === whatsappId);
    if (selectedWpp && selectedWpp.channel === "whatsapp_oficial") {
      setIsOficialSelected(true);
      // Buscar templates aprovados desta conexão
      api.get(`/quick-messages/list`, { 
        params: { companyId, isOficial: true, whatsappId: whatsappId } 
      }).then(({ data }) => {
        const approved = data.filter(t => t.status === "APPROVED");
        setOficialTemplates(approved);
      }).catch(err => {
        console.error("Erro ao buscar templates:", err);
        setOficialTemplates([]);
      });
    } else {
      setIsOficialSelected(false);
      setOficialTemplates([]);
      setSelectedTemplateId(null);
    }
  }, [whatsappId, whatsapps, companyId]);

  useEffect(() => {
    const now = moment();
    const scheduledAt = moment(campaign.scheduledAt);
    const moreThenAnHour =
      !Number.isNaN(scheduledAt.diff(now)) && scheduledAt.diff(now, "hour") > 1;
    const isEditable =
      campaign.status === "INATIVA" ||
      (campaign.status === "PROGRAMADA" && moreThenAnHour);

    setCampaignEditable(isEditable);
  }, [campaign.status, campaign.scheduledAt]);

  const handleClose = () => {
    onClose();
    setCampaign(initialState);
    setPreviewExecutions([]);
    setShowPreview(false);
    setSelectedTemplateId(null);
    setIsOficialSelected(false);
    setOficialTemplates([]);
    setTemplateVarValuesByTemplate({});
    setMessageMedias({});
    setSavedMessageMedias({});
    setMessageTemplates({});
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setAttachment(file);
    }
  };

  const handleSaveCampaign = async (values) => {
    if (isOficialSelected && !selectedTemplateId) {
      toast.error(i18n.t("campaignModal.selectTemplateRequired"));
      return;
    }

    // Validação: bloquear envio quando algum {{placeholder}} do template estiver vazio
    if (isOficialSelected && selectedTemplateId) {
      const tpl = oficialTemplates.find(t => t.id === selectedTemplateId);
      if (tpl) {
        const SECTION_LABELS = {
          header: "Cabeçalho",
          body: "Corpo",
          footer: "Rodapé",
          buttons: "Botões",
        };
        const varsMap = new Map();
        const collect = (sectionKey, text) => {
          if (!text) return;
          const localRe = /\{\{([^}]+)\}\}/g;
          let m;
          while ((m = localRe.exec(text)) !== null) {
            const key = m[1].trim();
            if (!key) continue;
            const entry = varsMap.get(key) || { key, sections: [] };
            if (!entry.sections.includes(sectionKey)) entry.sections.push(sectionKey);
            varsMap.set(key, entry);
          }
        };
        (tpl.components || []).forEach(c => {
          const t = (c.type || "").toLowerCase();
          if (["header", "body", "footer"].includes(t)) {
            collect(t, c.text || "");
          } else if (t === "buttons" && c.buttons) {
            try {
              const parsed = typeof c.buttons === "string" ? JSON.parse(c.buttons) : c.buttons;
              const arr = Array.isArray(parsed) ? parsed : [parsed];
              arr.forEach(b => {
                collect("buttons", b?.text || "");
                collect("buttons", b?.url || "");
              });
            } catch (_) { /* ignore */ }
          }
        });
        const missing = Array.from(varsMap.values()).filter(v => {
          const val = templateVarValues[v.key];
          return !val || !String(val).trim();
        });
        if (missing.length > 0) {
          const list = missing
            .map(v => `{{${v.key}}} (${v.sections.map(s => SECTION_LABELS[s] || s).join(" + ")})`)
            .join(", ");
          toast.error(`Preencha as variáveis obrigatórias do template: ${list}`);
          return;
        }
      }
    }

    try {
      const dataValues = {
        ...values,
        whatsappId: whatsappId,
        templateId: isOficialSelected ? selectedTemplateId : null,
        templateVariables: isOficialSelected && Object.keys(templateVarValues).length > 0
          ? JSON.stringify(templateVarValues)
          : null,
        userId: selectedUser?.id || null,
        queueId: selectedQueue || null,
        recurrenceDaysOfWeek: (values.isRecurring && values.recurrenceDaysOfWeek && values.recurrenceDaysOfWeek.length > 0)
          ? values.recurrenceDaysOfWeek
          : null,
        messageDelays: values.messageDelays ? JSON.stringify(values.messageDelays) : null,
        messageTemplates: messageTemplates && Object.keys(messageTemplates).length > 0
          ? JSON.stringify(messageTemplates)
          : null,
      };

      // API Oficial não suporta follow-up condicional — apenas o template é disparado
      if (isOficialSelected) {
        dataValues.sendFollowUpMessage = false;
      }

      Object.entries(values).forEach(([key, value]) => {
        if (key === "sendFollowUpMessage" && isOficialSelected) {
          dataValues[key] = false;
          return;
        }
        if (key === "scheduledAt" && value !== "" && value !== null) {
          // Interpreta o horário digitado como sendo no fuso do usuário e envia em UTC ao backend.
          dataValues[key] = moment.tz(value, userTimezone).toISOString();
        } else if (key === "recurrenceEndDate" && value !== "" && value !== null) {
          dataValues[key] = moment.tz(value, userTimezone).toISOString();
        } else if (key !== "recurrenceDaysOfWeek") {
          dataValues[key] = value === "" ? null : value;
        }
      });

      if (!values.isRecurring) {
        dataValues.recurrenceType = null;
        dataValues.recurrenceInterval = null;
        dataValues.recurrenceDaysOfWeek = null;
        dataValues.recurrenceDayOfMonth = null;
        dataValues.recurrenceEndDate = null;
        dataValues.maxExecutions = null;
      }

      // Se não tem contactListId nem tagListId válida, mas tem contatos individuais selecionados,
      // criar automaticamente um grupo de contatos
      if (!dataValues.contactListId && (!dataValues.tagListId || dataValues.tagListId === "none") && individualContacts.length > 0) {
        const listName = i18n.t("campaignModal.autoListName", { name: dataValues.name || i18n.t("campaignModal.noName"), date: moment().format("DD/MM HH:mm") });
        const { data: newList } = await api.post("/contact-lists/from-contacts", {
          name: listName,
          contactIds: individualContacts,
        });
        dataValues.contactListId = newList.id;
      }

      const uploadPerMessageMedias = async (campId) => {
        for (const [msgKey, file] of Object.entries(messageMedias)) {
          if (file) {
            const formData = new FormData();
            formData.append("file", file);
            await api.post(`/campaigns/${campId}/message-media-upload?messageKey=${msgKey}`, formData);
          }
        }
      };

      if (campaignId) {
        await api.put(`/campaigns/${campaignId}`, dataValues);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/campaigns/${campaignId}/media-upload`, formData);
        }
        await uploadPerMessageMedias(campaignId);
        handleClose();
      } else {
        const { data } = await api.post("/campaigns", dataValues);
        if (attachment != null) {
          const formData = new FormData();
          formData.append("file", attachment);
          await api.post(`/campaigns/${data.id}/media-upload`, formData);
        }
        await uploadPerMessageMedias(data.id);
        if (onSave) {
          onSave(data);
        }
        handleClose();
      }
      toast.success(i18n.t("campaigns.toasts.success"));
    } catch (err) {
      console.log(err);
      toastError(err);
    }
  };

  const deleteMedia = async () => {
    if (attachment) {
      setAttachment(null);
      attachmentFile.current.value = null;
    }

    if (campaign.mediaPath) {
      await api.delete(`/campaigns/${campaign.id}/media-upload`);
      setCampaign((prev) => ({ ...prev, mediaPath: null, mediaName: null }));
      toast.success(i18n.t("campaigns.toasts.deleted"));
    }
  };

  // Detecta se um template aprovado é "simples" (sem variáveis {{N}}). Restringimos
  // os slots de follow-up a templates simples para evitar pedir variáveis por slot.
  const isSimpleTemplate = (tpl) => {
    if (!tpl) return false;
    const re = /\{\{\d+\}\}/;
    for (const c of (tpl.components || [])) {
      const t = (c.type || "").toLowerCase();
      if (["header", "body", "footer"].includes(t) && re.test(c.text || "")) return false;
    }
    return true;
  };
  const simpleTemplates = oficialTemplates.filter(isSimpleTemplate);

  const renderMessageField = (identifier, showDelay = false, delayValue, onDelayChange) => {
    const slotTemplate = messageTemplates[identifier];
    const isTemplateMode = !!slotTemplate;
    const canUseTemplate = isOficialSelected;
    const setSlotMode = (toTemplate) => {
      setMessageTemplates((prev) => {
        const copy = { ...prev };
        if (toTemplate) copy[identifier] = { templateId: null, name: "" };
        else delete copy[identifier];
        return copy;
      });
    };
    const setSlotTemplateId = (templateId) => {
      const tpl = simpleTemplates.find((t) => t.id === templateId);
      setMessageTemplates((prev) => ({
        ...prev,
        [identifier]: { templateId: templateId || null, name: tpl?.shortcode || "" }
      }));
    };

    const hasSavedMedia = savedMessageMedias[identifier];
    const hasLocalMedia = messageMedias[identifier];
    const mediaInfo = hasLocalMedia ? { name: hasLocalMedia.name } : hasSavedMedia ? { name: hasSavedMedia.name } : null;

    return (
      <Box style={{ border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #e0e0e0", borderRadius: 8, padding: 12, marginBottom: 8, backgroundColor: isDark ? "#161b22" : "#fafafa" }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="caption" style={{ fontWeight: 600, textTransform: "uppercase", color: isDark ? "#9aa0a6" : "#666" }}>
            {i18n.t(`campaigns.dialog.form.${identifier}`)}
          </Typography>
          <Box display="flex" alignItems="center" style={{ gap: 8 }}>
            {canUseTemplate && (
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={isTemplateMode}
                    onChange={(e) => setSlotMode(e.target.checked)}
                    disabled={!campaignEditable}
                    color="primary"
                  />
                }
                label={<span style={{ fontSize: 11 }}>{i18n.t("campaignModal.followUp.useApprovedTemplate")}</span>}
                style={{ marginRight: 0 }}
              />
            )}
            {showDelay && (
              <FormControl variant="outlined" size="small" style={{ minWidth: 130 }}>
                <Select
                  value={delayValue || 3}
                  onChange={(e) => onDelayChange && onDelayChange(e.target.value)}
                  disabled={!campaignEditable}
                  style={{ fontSize: 12, height: 28 }}
                >
                  <MenuItem value={3}>{i18n.t("campaignModal.followUp.delay3s")}</MenuItem>
                  <MenuItem value={5}>{i18n.t("campaignModal.followUp.delay5s")}</MenuItem>
                  <MenuItem value={10}>{i18n.t("campaignModal.followUp.delay10s")}</MenuItem>
                  <MenuItem value={30}>{i18n.t("campaignModal.followUp.delay30s")}</MenuItem>
                  <MenuItem value={60}>{i18n.t("campaignModal.followUp.delay60s")}</MenuItem>
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>
        {isTemplateMode ? (
          <>
            <FormControl fullWidth variant="outlined" size="small">
              <Select
                value={slotTemplate?.templateId || ""}
                onChange={(e) => setSlotTemplateId(e.target.value)}
                disabled={!campaignEditable}
                displayEmpty
              >
                <MenuItem value="" disabled>
                  {simpleTemplates.length === 0
                    ? i18n.t("campaignModal.followUp.noSimpleTemplate")
                    : i18n.t("campaignModal.followUp.selectApprovedTemplate")}
                </MenuItem>
                {simpleTemplates.map((tpl) => (
                  <MenuItem key={tpl.id} value={tpl.id}>{tpl.shortcode}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginTop: 6, fontSize: 11 }}>
              {i18n.t("campaignModal.followUp.simpleTemplateHelper")}
            </Typography>
          </>
        ) : (
          <>
        <Field
          as={TextField}
          id={identifier}
          name={identifier}
          fullWidth
          rows={3}
          placeholder={i18n.t("campaigns.dialog.form.messagePlaceholder")}
          multiline={true}
          variant="outlined"
          size="small"
          helperText={i18n.t("campaignModal.messageVariableHelper")}
          disabled={!campaignEditable && campaign.status !== "CANCELADA"}
        />
        {/* Per-message media - sempre visível em cada mensagem */}
        {(
          <Box display="flex" alignItems="center" mt={1} style={{ gap: 6 }}>
            <input
              type="file"
              style={{ display: "none" }}
              ref={(el) => { messageMediaRefs.current[identifier] = el; }}
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  setMessageMedias((prev) => ({ ...prev, [identifier]: file }));
                }
              }}
            />
            {!mediaInfo && campaignEditable && (
              <Button
                size="small"
                variant="text"
                startIcon={<AttachFileIcon style={{ fontSize: 16 }} />}
                onClick={() => messageMediaRefs.current[identifier]?.click()}
                style={{ fontSize: 11, textTransform: "none", padding: "2px 8px" }}
              >
                {i18n.t("campaignModal.attachMedia")}
              </Button>
            )}
            {mediaInfo && (
              <>
                <Chip
                  size="small"
                  icon={<AttachFileIcon style={{ fontSize: 14 }} />}
                  label={mediaInfo.name}
                  onDelete={campaignEditable ? () => {
                    setMessageMedias((prev) => {
                      const copy = { ...prev };
                      delete copy[identifier];
                      return copy;
                    });
                    if (savedMessageMedias[identifier] && campaignId) {
                      api.delete(`/campaigns/${campaignId}/message-media?messageKey=${identifier}`)
                        .then(() => {
                          setSavedMessageMedias((prev) => {
                            const copy = { ...prev };
                            delete copy[identifier];
                            return copy;
                          });
                        })
                        .catch((err) => toastError(err));
                    } else {
                      setSavedMessageMedias((prev) => {
                        const copy = { ...prev };
                        delete copy[identifier];
                        return copy;
                      });
                    }
                  } : undefined}
                  style={{ fontSize: 11 }}
                />
              </>
            )}
          </Box>
        )}
          </>
        )}
      </Box>
    );
  };

  const renderConfirmationMessageField = (identifier) => {
    return (
      <Field
        as={TextField}
        id={identifier}
        name={identifier}
        fullWidth
        rows={5}
        label={i18n.t(`campaigns.dialog.form.${identifier}`)}
        placeholder={i18n.t("campaigns.dialog.form.messagePlaceholder")}
        multiline={true}
        variant="outlined"
        disabled={!campaignEditable && campaign.status !== "CANCELADA"}
      />
    );
  };

  const cancelCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/cancel`);
      toast.success(i18n.t("campaigns.toasts.cancel"));
      setCampaign((prev) => ({ ...prev, status: "CANCELADA" }));
      resetPagination();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const restartCampaign = async () => {
    try {
      await api.post(`/campaigns/${campaign.id}/restart`);
      toast.success(i18n.t("campaigns.toasts.restart"));
      setCampaign((prev) => ({ ...prev, status: "EM_ANDAMENTO" }));
      resetPagination();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const filterOptions = createFilterOptions({
    trim: true,
  });

  return (
    <div className={classes.root}>
      {/* ContactGroupModal movido para dentro do Formik para ter acesso a setFieldValue */}
      <ConfirmationModal
        title={i18n.t("campaigns.confirmationModal.deleteTitle")}
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={deleteMedia}
      >
        {i18n.t("campaigns.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        PaperProps={{
          style: {
            maxHeight: "92vh",
            width: "92vw",
          }
        }}
      >
        <DialogTitle id="form-dialog-title">
          {campaignEditable ? (
            <>
              {campaignId
                ? `${i18n.t("campaigns.dialog.update")}`
                : `${i18n.t("campaigns.dialog.new")}`}
            </>
          ) : (
            <>{`${i18n.t("campaigns.dialog.readonly")}`}</>
          )}
        </DialogTitle>
        <div style={{ display: "none" }}>
          <input
            type="file"
            ref={attachmentFile}
            onChange={(e) => handleAttachmentFile(e)}
          />
        </div>
        <Formik
          initialValues={campaign}
          enableReinitialize={true}
          validationSchema={CampaignSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveCampaign(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, errors, touched, isSubmitting, setFieldValue }) => (
            <Form>
              <ContactGroupModal
                open={contactGroupModalOpen}
                onClose={() => setContactGroupModalOpen(false)}
                onGroupCreated={(newGroup) => {
                  // Adicionar o novo grupo à lista e auto-selecionar
                  setContactLists((prev) => [...prev, newGroup]);
                  setFieldValue("contactListId", newGroup.id);
                  // Recarregar lista completa para garantir consistência
                  api.get(`/contact-lists/list`, { params: { companyId } })
                    .then(({ data }) => {
                      setContactLists(data);
                      // Manter seleção do novo grupo
                      setFieldValue("contactListId", newGroup.id);
                    });
                }}
              />
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                  value={messageTab}
                  onChange={(e, val) => setMessageTab(val)}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                >
                  <Tab label={i18n.t("campaignModal.tabCampaign")} />
                  <Tab label={i18n.t("campaignModal.tabContactLists")} />
                  <Tab label={i18n.t("campaignModal.tabSettings")} />
                </Tabs>
              </Box>

              {/* ABA 0: Configurações da Campanha */}
              <div hidden={messageTab !== 0}>
              <DialogContent dividers>
                <Grid spacing={2} container>
                  <Grid xs={12} md={4} item>
                    <Field
                      as={TextField}
                      label={i18n.t("campaigns.dialog.form.name")}
                      name="name"
                      error={touched.name && Boolean(errors.name)}
                      helperText={touched.name && errors.name}
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.textField}
                      disabled={!campaignEditable}
                    />
                  </Grid>
                  

                  {/* Tag selector movido para CampaignContactSelector */}

                  <Grid xs={12} md={4} item>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.formControl}
                    >
                      <InputLabel id="whatsapp-selection-label">
                        {i18n.t("campaigns.dialog.form.whatsapp")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.whatsapp")}
                        placeholder={i18n.t("campaigns.dialog.form.whatsapp")}
                        labelId="whatsapp-selection-label"
                        id="whatsappIds"
                        name="whatsappIds"
                        required
                        error={touched.whatsappId && Boolean(errors.whatsappId)}
                        disabled={!campaignEditable}
                        value={whatsappId}
                        onChange={(event) => {
                          setWhatsappId(event.target.value)
                        }}
                      >
                        {whatsapps &&
                          whatsapps.map((whatsapp) => (
                            <MenuItem key={whatsapp.id} value={whatsapp.id}>
                              {whatsapp.name}
                              {whatsapp.channel === "whatsapp_oficial" && (
                                <Chip 
                                  size="small" 
                                  label={i18n.t("campaignModal.apiOfficialLabel")} 
                                  style={{ 
                                    marginLeft: 8, 
                                    backgroundColor: "#25d366", 
                                    color: "#fff",
                                    height: 20,
                                    fontSize: '0.7rem'
                                  }} 
                                />
                              )}
                            </MenuItem>
                          ))}
                      </Field>
                    </FormControl>
                  </Grid>

                  {/* Seletor de Template - Visível para API Oficial (opcional) */}
                  {isOficialSelected && (
                    <Grid xs={12} md={4} item>
                      <FormControl
                        variant="outlined"
                        margin="dense"
                        fullWidth
                        className={classes.formControl}
                      >
                        <InputLabel id="template-selection-label">
                          {i18n.t("campaignModal.template.label")}
                        </InputLabel>
                        <Select
                          label={i18n.t("campaignModal.template.label")}
                          labelId="template-selection-label"
                          id="templateId"
                          value={selectedTemplateId || ""}
                          onChange={(e) => {
                            setSelectedTemplateId(e.target.value);
                          }}
                          disabled={!campaignEditable}
                          required
                        >
                          {oficialTemplates.map((template) => (
                            <MenuItem key={template.id} value={template.id}>
                              {template.shortcode} ({template.category})
                            </MenuItem>
                          ))}
                        </Select>
                        <FormHelperText>
                          {oficialTemplates.length === 0 
                            ? i18n.t("campaignModal.noTemplateFound")
                            : i18n.t("campaignModal.templateRequired")}
                        </FormHelperText>
                      </FormControl>
                      {(() => {
                        const tpl = oficialTemplates.find(t => t.id === selectedTemplateId);
                        if (!tpl) return null;
                        const headerComp = (tpl.components || []).find(
                          c => (c.type || "").toLowerCase() === "header" &&
                               ["IMAGE", "VIDEO", "DOCUMENT"].includes((c.format || "").toUpperCase())
                        );
                        if (!headerComp) return null;
                        const hasOwnMedia = !!tpl.mediaPath;
                        const hasSample = !!headerComp.example;
                        const fmt = (headerComp.format || "").toUpperCase();
                        return (
                          <FormHelperText style={{ color: hasOwnMedia || hasSample ? "#2e7d32" : "#d32f2f", marginTop: 4 }}>
                            {hasOwnMedia
                              ? `📎 Mídia automática do template (${fmt}): ${tpl.mediaName || "anexada"}`
                              : hasSample
                                ? `📎 Mídia automática do template (${fmt}): exemplo aprovado pela Meta`
                                : `⚠️ Template exige header ${fmt}. Anexe um arquivo abaixo ou salve a mídia no template.`}
                          </FormHelperText>
                        );
                      })()}
                    </Grid>
                  )}

                  {isOficialSelected && selectedTemplateId && (() => {
                    const tpl = oficialTemplates.find(t => t.id === selectedTemplateId);
                    if (!tpl) return null;
                    const re = /\{\{([^}]+)\}\}/g;
                    const SECTION_LABELS = {
                      header: "Cabeçalho",
                      body: "Corpo",
                      footer: "Rodapé",
                      buttons: "Botões",
                    };
                    // Detecta placeholders dedupando por chave, mas registrando
                    // todas as seções em que cada variável aparece. Assim a mesma
                    // {{1}} usada em header e body gera UM campo controlando ambas.
                    const varsMap = new Map();
                    const collect = (sectionKey, text) => {
                      if (!text) return;
                      const localRe = /\{\{([^}]+)\}\}/g;
                      let m;
                      while ((m = localRe.exec(text)) !== null) {
                        const key = m[1].trim();
                        if (!key) continue;
                        const entry = varsMap.get(key) || { key, sections: [] };
                        if (!entry.sections.includes(sectionKey)) {
                          entry.sections.push(sectionKey);
                        }
                        varsMap.set(key, entry);
                      }
                    };
                    (tpl.components || []).forEach(c => {
                      const t = (c.type || "").toLowerCase();
                      if (["header", "body", "footer"].includes(t)) {
                        collect(t, c.text || "");
                      } else if (t === "buttons" && c.buttons) {
                        try {
                          const parsed = typeof c.buttons === "string"
                            ? JSON.parse(c.buttons)
                            : c.buttons;
                          const arr = Array.isArray(parsed) ? parsed : [parsed];
                          arr.forEach(b => {
                            collect("buttons", b?.text || "");
                            collect("buttons", b?.url || "");
                          });
                        } catch (_) { /* ignore */ }
                      }
                    });
                    const vars = Array.from(varsMap.values());
                    const renderWithVars = (text) => {
                      if (!text) return "";
                      return text.replace(/\{\{([^}]+)\}\}/g, (full, k) => {
                        const v = templateVarValues[k.trim()];
                        return v && String(v).trim() ? v : full;
                      });
                    };
                    const headerComp = (tpl.components || []).find(
                      c => (c.type || "").toLowerCase() === "header" && (c.format || "TEXT").toUpperCase() === "TEXT"
                    );
                    const bodyComp = (tpl.components || []).find(c => (c.type || "").toLowerCase() === "body");
                    const footerComp = (tpl.components || []).find(c => (c.type || "").toLowerCase() === "footer");
                    const buttonsComp = (tpl.components || []).find(c => (c.type || "").toLowerCase() === "buttons");
                    let buttonsArr = [];
                    if (buttonsComp?.buttons) {
                      try {
                        const parsed = typeof buttonsComp.buttons === "string"
                          ? JSON.parse(buttonsComp.buttons)
                          : buttonsComp.buttons;
                        buttonsArr = Array.isArray(parsed) ? parsed : [parsed];
                      } catch (_) { buttonsArr = []; }
                    }
                    const hasContent =
                      vars.length > 0 ||
                      headerComp?.text ||
                      bodyComp?.text ||
                      footerComp?.text ||
                      buttonsArr.length > 0;
                    if (!hasContent) return null;
                    return (
                      <Grid xs={12} item>
                        {vars.length > 0 && (
                        <Box style={{
                          border: "1px solid #e0e0e0",
                          borderRadius: 8,
                          padding: 12,
                          backgroundColor: "#fafafa",
                          marginTop: 4,
                        }}>
                          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                            Variáveis do template
                          </Typography>
                          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 12 }}>
                            Preencha os valores que substituirão os placeholders {"{{...}}"} no envio.
                          </Typography>
                          <Grid container spacing={2}>
                            {vars.map(v => (
                              <Grid item xs={12} sm={6} md={4} key={v.key}>
                                <TextField
                                  fullWidth
                                  size="small"
                                  variant="outlined"
                                  label={`{{${v.key}}}`}
                                  placeholder={`Valor para {{${v.key}}}`}
                                  helperText={
                                    v.sections.length > 1
                                      ? `Aparece em: ${v.sections.map(s => SECTION_LABELS[s] || s).join(" + ")}`
                                      : `Seção: ${SECTION_LABELS[v.sections[0]] || v.sections[0]}`
                                  }
                                  value={templateVarValues[v.key] || ""}
                                  onChange={(e) => {
                                    const value = e.target.value;
                                    setTemplateVarValues(prev => ({
                                      ...prev,
                                      [v.key]: value,
                                    }));
                                  }}
                                  disabled={!campaignEditable}
                                />
                              </Grid>
                            ))}
                          </Grid>
                        </Box>
                        )}

                        <Box style={{
                          marginTop: 12,
                          border: "1px solid #d6e3f5",
                          borderRadius: 8,
                          padding: 12,
                          backgroundColor: "#eef4ff",
                        }}>
                          <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8 }}>
                            Prévia da mensagem
                          </Typography>
                          <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
                            Visualização com as variáveis substituídas. Tokens não preenchidos aparecem como {"{{...}}"}.
                          </Typography>
                          <Box style={{
                            backgroundColor: "#fff",
                            borderRadius: 8,
                            padding: 12,
                            boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
                            border: "1px solid #e6ecf5",
                            maxWidth: 420,
                          }}>
                            {headerComp?.text && (
                              <Typography style={{ fontWeight: 700, fontSize: 14, marginBottom: 6, whiteSpace: "pre-wrap" }}>
                                {renderWithVars(headerComp.text)}
                              </Typography>
                            )}
                            {bodyComp?.text && (
                              <Typography style={{ fontSize: 14, color: "#222", whiteSpace: "pre-wrap", lineHeight: 1.4 }}>
                                {renderWithVars(bodyComp.text)}
                              </Typography>
                            )}
                            {footerComp?.text && (
                              <Typography style={{ fontSize: 12, color: "#888", marginTop: 8, whiteSpace: "pre-wrap" }}>
                                {renderWithVars(footerComp.text)}
                              </Typography>
                            )}
                            {buttonsArr.length > 0 && (
                              <Box style={{ marginTop: 10, borderTop: "1px solid #eee", paddingTop: 8, display: "flex", flexDirection: "column", gap: 4 }}>
                                {buttonsArr.map((b, i) => (
                                  <Box key={i} style={{
                                    textAlign: "center",
                                    color: "#1976d2",
                                    fontSize: 13,
                                    fontWeight: 500,
                                    padding: "6px 8px",
                                    border: "1px solid #e0e0e0",
                                    borderRadius: 4,
                                    backgroundColor: "#fafafa",
                                  }}>
                                    {renderWithVars(b?.text || b?.url || "Botão")}
                                  </Box>
                                ))}
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })()}


                  <Grid xs={12} md={4} item>
                    <Field
                      as={TextField}
                      label={i18n.t("campaigns.dialog.form.scheduledAt")}
                      name="scheduledAt"
                      error={touched.scheduledAt && Boolean(errors.scheduledAt)}
                      helperText={
                        (touched.scheduledAt && errors.scheduledAt) ||
                        `Fuso: ${userTimezone} (${moment.tz(userTimezone).format("Z")})`
                      }
                      variant="outlined"
                      margin="dense"
                      type="datetime-local"
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      className={classes.textField}
                      disabled={!campaignEditable}
                    />
                  </Grid>

                  {/* SEÇÃO DE RECORRÊNCIA */}
                  <Grid xs={12} item>
                    <Card className={classes.recurrenceCard}>
                      <CardContent>
                        <Box display="flex" alignItems="center" mb={2}>
                          <RepeatIcon className={classes.recurrenceIcon} />
                          <Typography variant="h6">
                            {i18n.t("campaignModal.recurrenceTitle")}
                          </Typography>
                        </Box>
                        
                        <Grid spacing={2} container>
                          <Grid xs={12} item>
                            <FormControlLabel
                              control={
                                <Field
                                  as={Switch}
                                  name="isRecurring"
                                  checked={values.isRecurring}
                                  onChange={(e) => {
                                    setFieldValue('isRecurring', e.target.checked);
                                    if (!e.target.checked) {
                                      setPreviewExecutions([]);
                                      setShowPreview(false);
                                    }
                                  }}
                                  disabled={!campaignEditable}
                                />
                              }
                              label={i18n.t("campaignModal.enableRecurrence")}
                            />
                          </Grid>

                          <Grid xs={12} item style={{ padding: 0 }}>
                          <Collapse in={values.isRecurring} style={{ width: "100%" }}>
                            <Grid spacing={2} container>
                              <Grid xs={12} md={3} item>
                                <FormControl
                                  variant="outlined"
                                  margin="dense"
                                  fullWidth
                                  error={touched.recurrenceType && Boolean(errors.recurrenceType)}
                                >
                                  <InputLabel>{i18n.t("campaignModal.recurrenceType")}</InputLabel>
                                  <Field
                                    as={Select}
                                    name="recurrenceType"
                                    label={i18n.t("campaignModal.recurrenceType")}
                                    disabled={!campaignEditable}
                                    onChange={(e) => {
                                      setFieldValue('recurrenceType', e.target.value);
                                      setFieldValue('recurrenceDaysOfWeek', []);
                                      setFieldValue('recurrenceDayOfMonth', 1);
                                    }}
                                  >
                                    <MenuItem value="minutely">{i18n.t("campaignModal.minutely")}</MenuItem>
                                    <MenuItem value="hourly">{i18n.t("campaignModal.hourly")}</MenuItem>
                                    <MenuItem value="daily">{i18n.t("campaignModal.daily")}</MenuItem>
                                    <MenuItem value="weekly">{i18n.t("campaignModal.weekly")}</MenuItem>
                                    <MenuItem value="biweekly">{i18n.t("campaignModal.biweekly")}</MenuItem>
                                    <MenuItem value="monthly">{i18n.t("campaignModal.monthly")}</MenuItem>
                                    <MenuItem value="yearly">{i18n.t("campaignModal.yearly")}</MenuItem>
                                  </Field>
                                  {touched.recurrenceType && errors.recurrenceType && (
                                    <FormHelperText error>{errors.recurrenceType}</FormHelperText>
                                  )}
                                </FormControl>
                              </Grid>

                              <Grid xs={12} md={3} item>
                                <Field
                                  as={TextField}
                                  name="recurrenceInterval"
                                  label={i18n.t("campaignModal.interval")}
                                  type="number"
                                  variant="outlined"
                                  margin="dense"
                                  fullWidth
                                  inputProps={{ min: 1 }}
                                  error={touched.recurrenceInterval && Boolean(errors.recurrenceInterval)}
                                  helperText={
                                    touched.recurrenceInterval && errors.recurrenceInterval ||
                                    i18n.t("campaignModal.everyInterval", {
                                      count: values.recurrenceInterval || 1,
                                      unit: values.recurrenceType === 'minutely' ? i18n.t("campaignModal.minuteUnit") :
                                        values.recurrenceType === 'hourly' ? i18n.t("campaignModal.hourUnit") :
                                        values.recurrenceType === 'daily' ? i18n.t("campaignModal.dayUnit") :
                                        values.recurrenceType === 'weekly' ? i18n.t("campaignModal.weekUnit") :
                                        values.recurrenceType === 'biweekly' ? i18n.t("campaignModal.biweekUnit") :
                                        values.recurrenceType === 'monthly' ? i18n.t("campaignModal.monthUnit") :
                                        values.recurrenceType === 'yearly' ? i18n.t("campaignModal.yearUnit") : ''
                                    })
                                  }
                                  disabled={!campaignEditable}
                                />
                              </Grid>

                              {/* Dias da semana para recorrência semanal */}
                              {values.recurrenceType === 'weekly' && (
                                <Grid xs={12} md={6} item>
                                  <Typography variant="subtitle2" gutterBottom>
                                    {i18n.t("campaignModal.daysOfWeek")}
                                  </Typography>
                                  <FormGroup row>
                                    {daysOfWeekOptions.map((day) => (
                                      <FormControlLabel
                                        key={day.value}
                                        control={
                                          <Checkbox
                                            checked={values.recurrenceDaysOfWeek.includes(day.value)}
                                            onChange={(e) => {
                                              const currentDays = values.recurrenceDaysOfWeek || [];
                                              if (e.target.checked) {
                                                setFieldValue('recurrenceDaysOfWeek', [...currentDays, day.value]);
                                              } else {
                                                setFieldValue('recurrenceDaysOfWeek', 
                                                  currentDays.filter(d => d !== day.value)
                                                );
                                              }
                                            }}
                                            disabled={!campaignEditable}
                                          />
                                        }
                                        label={day.label.substring(0, 3)}
                                      />
                                    ))}
                                  </FormGroup>
                                  {touched.recurrenceDaysOfWeek && errors.recurrenceDaysOfWeek && (
                                    <FormHelperText error>{errors.recurrenceDaysOfWeek}</FormHelperText>
                                  )}
                                </Grid>
                              )}

                              {/* Dia do mês para recorrência mensal */}
                              {values.recurrenceType === 'monthly' && (
                                <Grid xs={12} md={3} item>
                                  <Field
                                    as={TextField}
                                    name="recurrenceDayOfMonth"
                                    label={i18n.t("campaignModal.dayOfMonth")}
                                    type="number"
                                    variant="outlined"
                                    margin="dense"
                                    fullWidth
                                    inputProps={{ min: 1, max: 31 }}
                                    error={touched.recurrenceDayOfMonth && Boolean(errors.recurrenceDayOfMonth)}
                                    helperText={
                                      touched.recurrenceDayOfMonth && errors.recurrenceDayOfMonth ||
                                      i18n.t("campaignModal.dayOfMonthHelper")
                                    }
                                    disabled={!campaignEditable}
                                  />
                                </Grid>
                              )}

                              <Grid xs={12} md={4} item>
                                <Field
                                  as={TextField}
                                  name="recurrenceEndDate"
                                  label={i18n.t("campaignModal.endDate")}
                                  type="date"
                                  variant="outlined"
                                  margin="dense"
                                  fullWidth
                                  InputLabelProps={{ shrink: true }}
                                  error={touched.recurrenceEndDate && Boolean(errors.recurrenceEndDate)}
                                  helperText={
                                    touched.recurrenceEndDate && errors.recurrenceEndDate ||
                                    i18n.t("campaignModal.emptyRecurrence")
                                  }
                                  disabled={!campaignEditable}
                                />
                              </Grid>

                              <Grid xs={12} md={4} item>
                                <Field
                                  as={TextField}
                                  name="maxExecutions"
                                  label={i18n.t("campaignModal.maxExecutions")}
                                  type="number"
                                  variant="outlined"
                                  margin="dense"
                                  fullWidth
                                  inputProps={{ min: 1 }}
                                  error={touched.maxExecutions && Boolean(errors.maxExecutions)}
                                  helperText={
                                    touched.maxExecutions && errors.maxExecutions ||
                                    i18n.t("campaignModal.emptyRecurrence")
                                  }
                                  disabled={!campaignEditable}
                                />
                              </Grid>

                              <Grid xs={12} md={4} item>
                                <Button
                                  variant="outlined"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => {
                                    handlePreviewRecurrence(values);
                                    setShowPreview(!showPreview);
                                  }}
                                  disabled={!values.recurrenceType || !values.scheduledAt}
                                  fullWidth
                                  style={{ marginTop: 8 }}
                                >
                                  {showPreview ? i18n.t("campaignModal.hideExecutions") : i18n.t("campaignModal.showExecutions")} {i18n.t("campaignModal.nextExecutions")}
                                </Button>
                              </Grid>

                              {/* Preview das execuções */}
                              <Collapse in={showPreview && previewExecutions.length > 0}>
                                <Grid xs={12} item>
                                  <Card className={classes.previewCard}>
                                    <CardContent>
                                      <Typography variant="subtitle2" gutterBottom>
                                        {i18n.t("campaignModal.next5Executions")}
                                      </Typography>
                                      <List dense>
                                        {previewExecutions.slice(0, 5).map((execution, index) => (
                                          <ListItem key={index} divider>
                                            <ListItemText
                                              primary={i18n.t("campaignModal.nthExecution", { n: index + 1 })}
                                              secondary={typeof execution === 'string' ? execution : moment(execution).format('DD/MM/YYYY HH:mm')}
                                            />
                                          </ListItem>
                                        ))}
                                      </List>
                                    </CardContent>
                                  </Card>
                                </Grid>
                              </Collapse>
                            </Grid>
                          </Collapse>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid xs={12} md={4} item>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.formControl}
                    >
                      <InputLabel id="openTicket-selection-label">
                        {i18n.t("campaigns.dialog.form.openTicket")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.openTicket")}
                        placeholder={i18n.t("campaigns.dialog.form.openTicket")}
                        labelId="openTicket-selection-label"
                        id="openTicket"
                        name="openTicket"
                        error={touched.openTicket && Boolean(errors.openTicket)}
                        disabled={!campaignEditable}
                      >
                        <MenuItem value={"enabled"}>{i18n.t("campaigns.dialog.form.enabledOpenTicket")}</MenuItem>
                        <MenuItem value={"disabled"}>{i18n.t("campaigns.dialog.form.disabledOpenTicket")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  {/* SELECIONAR USUARIO */}
                  <Grid xs={12} md={4} item>
                    <Autocomplete
                      style={{ marginTop: '8px' }}
                      variant="outlined"
                      margin="dense"
                      className={classes.formControl}
                      getOptionLabel={(option) => `${option.name}`}
                      value={selectedUser}
                      size="small"
                      onChange={(e, newValue) => {
                        setSelectedUser(newValue);
                        if (newValue != null && Array.isArray(newValue.queues)) {
                          if (newValue.queues.length === 1) {
                            setSelectedQueue(newValue.queues[0].id);
                          }
                          setQueues(newValue.queues);
                        } else {
                          setQueues(allQueues);
                          setSelectedQueue("");
                        }
                      }}
                      options={options}
                      filterOptions={filterOptions}
                      freeSolo
                      fullWidth
                      autoHighlight
                      disabled={!campaignEditable || values.openTicket === 'disabled'}
                      noOptionsText={i18n.t("transferTicketModal.noOptions")}
                      loading={loading}
                      renderOption={option => (<span> <UserStatusIcon user={option} /> {option.name}</span>)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={i18n.t("transferTicketModal.fieldLabel")}
                          variant="outlined"
                          onChange={(e) => setSearchParam(e.target.value)}
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <React.Fragment>
                                {loading ? (
                                  <CircularProgress color="inherit" size={20} />
                                ) : null}
                                {params.InputProps.endAdornment}
                              </React.Fragment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid xs={12} md={4} item>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.formControl}
                    >
                      <InputLabel>
                        {i18n.t("transferTicketModal.fieldQueueLabel")}
                      </InputLabel>
                      <Select
                        value={selectedQueue}
                        onChange={(e) => setSelectedQueue(e.target.value)}
                        label={i18n.t("transferTicketModal.fieldQueuePlaceholder")}
                        required={!isNil(selectedUser)}
                        disabled={!campaignEditable || values.openTicket === 'disabled'}
                      >
                        {queues.map((queue) => (
                          <MenuItem key={queue.id} value={queue.id}>
                            {queue.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid xs={12} md={4} item>
                    <FormControl
                      variant="outlined"
                      margin="dense"
                      fullWidth
                      className={classes.formControl}
                    >
                      <InputLabel id="statusTicket-selection-label">
                        {i18n.t("campaigns.dialog.form.statusTicket")}
                      </InputLabel>
                      <Field
                        as={Select}
                        label={i18n.t("campaigns.dialog.form.statusTicket")}
                        placeholder={i18n.t("campaigns.dialog.form.statusTicket")}
                        labelId="statusTicket-selection-label"
                        id="statusTicket"
                        name="statusTicket"
                        error={touched.statusTicket && Boolean(errors.statusTicket)}
                        disabled={!campaignEditable || values.openTicket === 'disabled'}
                      >
                        <MenuItem value={"closed"}>{i18n.t("campaigns.dialog.form.closedTicketStatus")}</MenuItem>
                        <MenuItem value={"pending"}>{i18n.t("campaigns.dialog.form.pendingTicketStatus")}</MenuItem>
                        <MenuItem value={"open"}>{i18n.t("campaigns.dialog.form.openTicketStatus")}</MenuItem>
                      </Field>
                    </FormControl>
                  </Grid>

                  {/* LAYOUT DUAS COLUNAS: Mensagem + Preview */}
                  <Grid xs={12} item>
                    <Grid spacing={2} container>
                      {/* Coluna Esquerda - Campo de Mensagem */}
                      <Grid xs={12} md={6} item>
                        <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 600 }}>
                          {i18n.t("campaignModal.campaignMessage")}
                        </Typography>
                        {/* Chips de variáveis clicáveis - ocultos para API Oficial */}
                        {!isOficialSelected && (
                        <Box display="flex" flexWrap="wrap" style={{ gap: 4, marginBottom: 8 }}>
                          {[
                            { label: i18n.t("campaignModal.varName"), value: "{nome}" },
                            { label: i18n.t("campaignModal.varNumber"), value: "{numero}" },
                            { label: i18n.t("campaignModal.varEmail"), value: "{email}" },
                            { label: i18n.t("campaignModal.varGreeting"), value: "{greeting}" },
                            { label: i18n.t("campaignModal.varProtocol"), value: "{protocol}" },
                            { label: i18n.t("campaignModal.varFirstName"), value: "{primeiroNome}" },
                            { label: i18n.t("campaignModal.varAgent"), value: "{atendente}" },
                          ].map((v) => (
                            <Chip
                              key={v.value}
                              label={v.label}
                              size="small"
                              color="primary"
                              variant="outlined"
                              clickable
                              disabled={!campaignEditable && campaign.status !== "CANCELADA"}
                              onClick={() => {
                                const field = document.getElementById("message1");
                                if (field) {
                                  const start = field.selectionStart || 0;
                                  const end = field.selectionEnd || 0;
                                  const current = values.message1 || "";
                                  const newVal = current.substring(0, start) + v.value + current.substring(end);
                                  setFieldValue("message1", newVal);
                                  setTimeout(() => {
                                    field.focus();
                                    const pos = start + v.value.length;
                                    field.setSelectionRange(pos, pos);
                                  }, 50);
                                } else {
                                  setFieldValue("message1", (values.message1 || "") + v.value);
                                }
                              }}
                              style={{ cursor: "pointer" }}
                            />
                          ))}
                        </Box>
                        )}
                        {isOficialSelected ? (
                          <Box>
                            <Box p={2} style={{ backgroundColor: isDark ? "#1a1c2e" : "#f5f5f5", borderRadius: 8, textAlign: "center", marginBottom: 12 }}>
                              <Typography variant="body2" color="textSecondary">
                                {i18n.t("campaignModal.apiOfficialNote")}
                              </Typography>
                            </Box>
                            <Box
                              p={1.5}
                              style={{
                                backgroundColor: isDark ? "#3d2200" : "#fff3e0",
                                border: isDark ? "1px solid #e65100" : "1px solid #ffb74d",
                                borderRadius: 8,
                                marginBottom: 12,
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 8
                              }}
                            >
                              <Typography
                                variant="body2"
                                style={{ color: isDark ? "#ffb74d" : "#e65100", fontSize: 12, lineHeight: 1.4 }}
                              >
                                {i18n.t("campaignModal.apiOfficialNoFollowUp")}
                              </Typography>
                            </Box>
                          </Box>
                        ) : values.confirmation ? (
                          <Grid spacing={2} container>
                            <Grid xs={12} item>
                              <>{renderMessageField("message1")}</>
                            </Grid>
                            <Grid xs={12} item>
                              <>{renderConfirmationMessageField("confirmationMessage1")}</>
                            </Grid>
                          </Grid>
                        ) : (
                          <>{renderMessageField("message1")}</>
                        )}

                      </Grid>

                      {/* Coluna Direita - Preview */}
                      <Grid xs={12} md={6} item>
                        <Typography variant="subtitle2" style={{ marginBottom: 8, fontWeight: 600 }}>
                          {i18n.t("campaignModalExtra.preview")}
                        </Typography>
                        {isOficialSelected && selectedTemplateId ? (
                          <TemplatePreview
                            template={oficialTemplates.find(t => t.id === selectedTemplateId)}
                            attachment={attachment}
                            onVariablesChange={setTemplateVarValues}
                          />
                        ) : (
                          <MessagePreview
                            messages={values}
                            attachment={attachment}
                            mediaPath={campaign.mediaPath}
                            mediaName={campaign.mediaName}
                          />
                        )}
                      </Grid>
                    </Grid>
                    </Grid>
                  {/* Seletor simples de lista de contatos e tags */}
                  <Grid xs={12} item>
                    <Box style={{ border: isDark ? "1px solid rgba(255,255,255,0.12)" : "1px solid #e0e0e0", borderRadius: 8, padding: "12px 16px", backgroundColor: isDark ? "#161b22" : undefined }}>
                      <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
                        {i18n.t("campaignModal.contactListTitle")}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" style={{ fontSize: 12, marginBottom: 12 }}>
                        {i18n.t("campaignModal.contactListDesc")}
                      </Typography>
                      <Grid spacing={1} container>
                        <Grid xs={12} md={6} item>
                          <FormControl variant="outlined" size="small" fullWidth disabled={!campaignEditable}>
                            <InputLabel id="contactListId-label">{i18n.t("campaignModal.contactList")}</InputLabel>
                            <Select
                              labelId="contactListId-label"
                              label={i18n.t("campaignModal.contactList")}
                              value={values.contactListId || ""}
                              onChange={(e) => setFieldValue("contactListId", e.target.value)}
                            >
                              <MenuItem value="">
                                <em>{i18n.t("campaignModal.noListSelected")}</em>
                              </MenuItem>
                              {contactLists.map((cl) => (
                                <MenuItem key={cl.id} value={cl.id}>
                                  {cl.name}
                                  {cl.contactsCount !== undefined && (
                                    <Typography component="span" style={{ fontSize: 11, color: "#999", marginLeft: 8 }}>
                                      ({cl.contactsCount} {i18n.t("campaignModal.contacts")})
                                    </Typography>
                                  )}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid xs={12} md={6} item>
                          <FormControl variant="outlined" size="small" fullWidth disabled={!campaignEditable}>
                            <InputLabel id="tagListId-label">{i18n.t("campaignModal.filterByTag")}</InputLabel>
                            <Select
                              labelId="tagListId-label"
                              label={i18n.t("campaignModal.filterByTag")}
                              value={values.tagListId || "none"}
                              onChange={(e) => setFieldValue("tagListId", e.target.value)}
                            >
                              <MenuItem value="none">
                                <em>{i18n.t("campaignModal.noTagFilter")}</em>
                              </MenuItem>
                              {tagLists.map((tl) => (
                                <MenuItem key={tl.id} value={tl.id}>
                                  {tl.name}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                      <Box mt={1} display="flex" style={{ gap: 8, flexWrap: "wrap" }}>
                        {values.contactListId && (
                          <Button
                            size="small"
                            style={{ fontSize: 11, textTransform: "none" }}
                            onClick={() => { setFieldValue("contactListId", ""); setMessageTab(1); }}
                          >
                            {i18n.t("campaignModal.switchList")}
                          </Button>
                        )}
                        {!values.contactListId && campaignEditable && (
                          <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            style={{ fontSize: 11, textTransform: "none" }}
                            onClick={() => setMessageTab(1)}
                          >
                            {i18n.t("campaignModal.createOrSelectList")}
                          </Button>
                        )}
                      </Box>
                    </Box>
                  </Grid>
                </Grid>
              </DialogContent>
              </div>

              {/* ABA 1: Listas de Contatos */}
              <div hidden={messageTab !== 1}>
                <DialogContent dividers>
                  <CampaignContactSelector
                    contactListId={values.contactListId}
                    tagListId={values.tagListId}
                    tagLists={tagLists}
                    contactLists={contactLists}
                    onTagChange={(val) => setFieldValue("tagListId", val)}
                    onContactListChange={(val) => {
                      setFieldValue("contactListId", val);
                      if (val) {
                        // Recarregar listas para ter dados atualizados
                        api.get(`/contact-lists/list`, { params: { companyId } })
                          .then(({ data }) => setContactLists(data))
                          .catch(() => {});
                      }
                    }}
                    onSelectedContactsChange={setIndividualContacts}
                    campaignEditable={campaignEditable}
                    campaignName={values.name}
                    whatsappId={whatsappId}
                    onGoToCampaignTab={() => setMessageTab(0)}
                  />
                </DialogContent>
              </div>

              {/* ABA 2: Configurações de Disparo */}
              <div hidden={messageTab !== 2}>
                <DialogContent dividers>
                    <Grid spacing={2} container>
                      <Grid xs={12} item>
                        <Typography variant="subtitle1" style={{ fontWeight: 600, marginBottom: 8 }}>
                          {i18n.t("campaignModal.dispatchIntervals")}
                        </Typography>
                        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                          {i18n.t("campaignModal.dispatchIntervalsDesc")}
                        </Typography>
                      </Grid>
                    <Grid xs={12} md={4} item>
                      <FormControl variant="outlined" fullWidth size="small">
                        <InputLabel id="cfg-messageInterval-label">
                          {i18n.t("campaigns.settings.randomInterval")}
                        </InputLabel>
                        <Select
                          labelId="cfg-messageInterval-label"
                          label={i18n.t("campaigns.settings.randomInterval")}
                          value={campaignSettings.messageInterval}
                          onChange={(e) => setCampaignSettings((p) => ({ ...p, messageInterval: Number(e.target.value) }))}
                        >
                          <MenuItem value={0}>{i18n.t("campaigns.settings.noBreak")}</MenuItem>
                          <MenuItem value={5}>5 {i18n.t("campaignModalExtra.seconds")}</MenuItem>
                          <MenuItem value={10}>10 {i18n.t("campaignModalExtra.seconds")}</MenuItem>
                          <MenuItem value={15}>15 {i18n.t("campaignModalExtra.seconds")}</MenuItem>
                          <MenuItem value={20}>20 {i18n.t("campaignModalExtra.seconds")}</MenuItem>
                          <MenuItem value={30}>30 {i18n.t("campaignModalExtra.seconds")}</MenuItem>
                          <MenuItem value={60}>60 {i18n.t("campaignModalExtra.seconds")}</MenuItem>
                          <MenuItem value={80}>80 {i18n.t("campaignModalExtra.seconds")}</MenuItem>
                          <MenuItem value={100}>100 {i18n.t("campaignModalExtra.seconds")}</MenuItem>
                          <MenuItem value={120}>120 {i18n.t("campaignModalExtra.seconds")}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={4} item>
                      <FormControl variant="outlined" fullWidth size="small">
                        <InputLabel id="cfg-longerIntervalAfter-label">
                          {i18n.t("campaigns.settings.intervalGapAfter")}
                        </InputLabel>
                        <Select
                          labelId="cfg-longerIntervalAfter-label"
                          label={i18n.t("campaigns.settings.intervalGapAfter")}
                          value={campaignSettings.longerIntervalAfter}
                          onChange={(e) => setCampaignSettings((p) => ({ ...p, longerIntervalAfter: Number(e.target.value) }))}
                        >
                          <MenuItem value={0}>{i18n.t("campaigns.settings.undefined")}</MenuItem>
                          <MenuItem value={5}>5 {i18n.t("campaigns.settings.messages")}</MenuItem>
                          <MenuItem value={10}>10 {i18n.t("campaigns.settings.messages")}</MenuItem>
                          <MenuItem value={20}>20 {i18n.t("campaigns.settings.messages")}</MenuItem>
                          <MenuItem value={30}>30 {i18n.t("campaigns.settings.messages")}</MenuItem>
                          <MenuItem value={50}>50 {i18n.t("campaigns.settings.messages")}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid xs={12} md={4} item>
                      <FormControl variant="outlined" fullWidth size="small">
                        <InputLabel id="cfg-greaterInterval-label">
                          {i18n.t("campaigns.settings.laggerTriggerRange")}
                        </InputLabel>
                        <Select
                          labelId="cfg-greaterInterval-label"
                          label={i18n.t("campaigns.settings.laggerTriggerRange")}
                          value={campaignSettings.greaterInterval}
                          onChange={(e) => setCampaignSettings((p) => ({ ...p, greaterInterval: Number(e.target.value) }))}
                        >
                          <MenuItem value={0}>{i18n.t("campaigns.settings.noBreak")}</MenuItem>
                          <MenuItem value={20}>20 {i18n.t("campaignModalExtra.seconds")}</MenuItem>
                          <MenuItem value={30}>30 {i18n.t("campaignModalExtra.seconds")}</MenuItem>
                          <MenuItem value={60}>60 {i18n.t("campaignModalExtra.seconds")}</MenuItem>
                          <MenuItem value={90}>90 {i18n.t("campaignModalExtra.seconds")}</MenuItem>
                          <MenuItem value={120}>120 {i18n.t("campaignModalExtra.seconds")}</MenuItem>
                          <MenuItem value={180}>180 {i18n.t("campaignModalExtra.seconds")}</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {/* Anti-Ban */}
                    <Grid xs={12} item>
                      <div style={{ borderTop: isDark ? "1px dashed rgba(255,255,255,0.12)" : "1px dashed #e0e0e0", margin: "16px 0 10px", display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: "#e65100", background: isDark ? "#3d2200" : "#fff3e0", borderRadius: 4, padding: "2px 10px" }}>🛡️ {i18n.t("campaignModal.antiBan")}</span>
                        <span style={{ fontSize: 11, color: isDark ? "#9aa0a6" : "#888" }}>{i18n.t("campaignModal.antiBanDesc")}</span>
                      </div>
                    </Grid>
                     <Grid xs={12} md={4} item>
                       <JitterSlider campaignSettings={campaignSettings} setCampaignSettings={setCampaignSettings} />
                     </Grid>
                     <Grid xs={12} md={4} item>
                       <FormControl variant="outlined" fullWidth size="small">
                          <InputLabel id="cfg-longPauseEvery-label">{i18n.t("campaignModal.extraPauseEvery")}</InputLabel>
                         <Select
                           labelId="cfg-longPauseEvery-label"
                           label={i18n.t("campaignModal.extraPauseEvery")}
                           value={campaignSettings.longPauseEvery !== undefined && campaignSettings.longPauseEvery !== null ? campaignSettings.longPauseEvery : 50}
                            onChange={(e) => {
                              setCampaignSettings((p) => ({ ...p, longPauseEvery: Number(e.target.value) }));
                            }}
                         >
                           <MenuItem value={0}>{i18n.t("campaignModal.disabled")}</MenuItem>
                           <MenuItem value={10}>{i18n.t("campaignModal.everyNMessages", { n: 10 })}</MenuItem>
                           <MenuItem value={20}>{i18n.t("campaignModal.everyNMessages", { n: 20 })}</MenuItem>
                           <MenuItem value={30}>{i18n.t("campaignModal.everyNMessages", { n: 30 })}</MenuItem>
                           <MenuItem value={50}>{i18n.t("campaignModal.everyNMessages", { n: 50 })}</MenuItem>
                           <MenuItem value={100}>{i18n.t("campaignModal.everyNMessages", { n: 100 })}</MenuItem>
                         </Select>
                       </FormControl>
                     </Grid>
                     <Grid xs={12} md={4} item>
                       <FormControl variant="outlined" fullWidth size="small">
                          <InputLabel id="cfg-longPauseDuration-label">{i18n.t("campaignModal.extraPauseDuration")}</InputLabel>
                         <Select
                           labelId="cfg-longPauseDuration-label"
                           label={i18n.t("campaignModal.extraPauseDuration")}
                           value={campaignSettings.longPauseDuration !== undefined && campaignSettings.longPauseDuration !== null ? campaignSettings.longPauseDuration : 30}
                           disabled={campaignSettings.longPauseEvery === 0}
                            onChange={(e) => {
                              setCampaignSettings((p) => ({ ...p, longPauseDuration: Number(e.target.value) }));
                            }}
                         >
                           <MenuItem value={15}>15 {i18n.t("campaignModalExtra.seconds")}</MenuItem>
                           <MenuItem value={30}>30 {i18n.t("campaignModalExtra.seconds")}</MenuItem>
                           <MenuItem value={60}>{i18n.t("campaignModal.minute1")}</MenuItem>
                           <MenuItem value={120}>{i18n.t("campaignModal.minutes2")}</MenuItem>
                           <MenuItem value={300}>{i18n.t("campaignModal.minutes5")}</MenuItem>
                         </Select>
                       </FormControl>
                     </Grid>

                    <Grid xs={12} item style={{ textAlign: "right" }}>
                      <Button
                        variant="contained"
                        color="primary"
                        disabled={savingSettings}
                        onClick={async () => {
                          setSavingSettings(true);
                          try {
                            await api.post("/campaign-settings", { settings: campaignSettings });
                            toast.success(i18n.t("campaignModal.settingsSaved"));
                          } catch (err) {
                            toastError(err);
                          } finally {
                            setSavingSettings(false);
                          }
                        }}
                      >
                        {savingSettings ? <CircularProgress size={20} color="inherit" /> : i18n.t("campaigns.settings.save")}
                      </Button>
                    </Grid>
                   </Grid>
                </DialogContent>
              </div>

              <DialogActions>
                {campaign.status === "CANCELADA" && (
                  <Button
                    color="primary"
                    onClick={() => restartCampaign()}
                    variant="outlined"
                  >
                    {i18n.t("campaigns.dialog.buttons.restart")}
                  </Button>
                )}
                {campaign.status === "EM_ANDAMENTO" && (
                  <Button
                    color="primary"
                    onClick={() => cancelCampaign()}
                    variant="outlined"
                  >
                    {i18n.t("campaigns.dialog.buttons.cancel")}
                  </Button>
                )}
                
                <Button
                  onClick={handleClose}
                  color="primary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("campaigns.dialog.buttons.close")}
                </Button>
                {(messageTab === 0 || messageTab === 1) && (campaignEditable || campaign.status === "CANCELADA") && (
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                    variant="contained"
                    className={classes.btnWrapper}
                  >
                    {campaignId
                      ? `${i18n.t("campaigns.dialog.buttons.edit")}`
                      : `${i18n.t("campaigns.dialog.buttons.add")}`}
                    {isSubmitting && (
                      <CircularProgress
                        size={24}
                        className={classes.buttonProgress}
                      />
                    )}
                  </Button>
                )}
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default CampaignModal;
